import { Button, Modal, ModalFooter } from 'components';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface MaintenanceProviderContextType {
  showMaintenanceDialog: (info?: MaintenanceInfo) => void;
}

const MaintenanceDialogContext = createContext<MaintenanceProviderContextType>({
  showMaintenanceDialog: () => {}
});

export interface MaintenanceInfo {
  detail: string;
  estimatedEndTime: string | null;
  retryAfterSeconds: number;
  status: number;
  title: string;
}

const MaintenanceProvider = ({ children }: PropsWithChildren) => {
  const { tCommon } = useCustomTranslation();

  const [maintenanceDialog, setMaintenanceDialog] = useState<{
    open: boolean;
    info?: MaintenanceInfo;
  } | null>(null);

  const showMaintenanceDialog = useCallback((info?: MaintenanceInfo) => {
    setMaintenanceDialog({ open: true, info });
  }, []);

  const value = useMemo<MaintenanceProviderContextType>(
    () => ({ showMaintenanceDialog }),
    [showMaintenanceDialog]
  );

  return (
    <MaintenanceDialogContext.Provider value={value}>
      <Modal
        onClose={() => setMaintenanceDialog(null)}
        open={maintenanceDialog?.open || false}
        title={maintenanceDialog?.info?.title}
      >
        <div className="MaintenanceDialog">
          <p>{maintenanceDialog?.info?.detail}</p>
          {maintenanceDialog?.info?.estimatedEndTime && (
            <span>
              {tCommon('maintenance_end_time', {
                estimatedEndTime: maintenanceDialog.info.estimatedEndTime
              })}
            </span>
          )}
          <ModalFooter>
            <Button type="primary" onClick={() => setMaintenanceDialog(null)}>
              {tCommon('maintenance_confirm_button')}
            </Button>
          </ModalFooter>
        </div>
      </Modal>
      {children}
    </MaintenanceDialogContext.Provider>
  );
};

export default MaintenanceProvider;

export function useMaintenanceDialog() {
  return useContext(MaintenanceDialogContext);
}
